import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sparkles, AlertTriangle, ShieldCheck, Users, Calendar,
  Loader2, ChevronRight, TrendingUp, Clock, X, RefreshCw, Play,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  coverage_gap: Calendar,
  burnout_risk: Clock,
  pto_conflict: AlertTriangle,
  understaffed: Users,
  overstaffed: TrendingUp,
  pattern_alert: Sparkles,
  shift_conflict: AlertTriangle,
};

const severityStyles = {
  critical: {
    border: 'border-l-red-500',
    bg: 'bg-red-50/60',
    badge: 'destructive',
    text: 'text-red-700',
  },
  warning: {
    border: 'border-l-amber-500',
    bg: 'bg-amber-50/60',
    badge: 'outline',
    text: 'text-amber-700',
  },
  info: {
    border: 'border-l-blue-400',
    bg: 'bg-blue-50/40',
    badge: 'secondary',
    text: 'text-blue-700',
  },
};

const AIProactiveInsights = ({ onChatAction }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());
  const [hasRun, setHasRun] = useState(false);

  // On mount, try to load cached insights (no LLM call)
  useEffect(() => {
    loadCached();
  }, []);

  const loadCached = async () => {
    try {
      const res = await axios.get(`${API}/ai/proactive`, { headers });
      if (res.data && !res.data.error && res.data.from_cache) {
        setData(res.data);
        setHasRun(true);
      }
    } catch {
      // No cached data — that's fine, user will click Run
    }
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/proactive?force=true`, { headers });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        setData(res.data);
        setDismissed(new Set());
        setHasRun(true);
      }
    } catch (err) {
      toast.error('Failed to generate insights');
    } finally {
      setLoading(false);
    }
  };

  const dismiss = (id) => setDismissed(prev => new Set([...prev, id]));

  const handleAction = (insight) => {
    if (onChatAction) {
      onChatAction(`How should I resolve: ${insight.title} — ${insight.description}`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card className="border-0 shadow-sm" data-testid="ai-proactive-loading">
        <CardContent className="py-8 flex items-center justify-center gap-3 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Analyzing upcoming schedule...</span>
        </CardContent>
      </Card>
    );
  }

  // Idle state — no data yet, show run button
  if (!hasRun || !data) {
    return (
      <Card className="border-0 shadow-sm" data-testid="ai-proactive-idle">
        <CardContent className="py-8 flex flex-col items-center justify-center gap-4">
          <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-indigo-400" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-700">AI Schedule Analysis</p>
            <p className="text-xs text-slate-400 mt-1">
              Analyze coverage gaps, staffing conflicts, and burnout risks for the next 2 weeks
            </p>
          </div>
          <Button
            onClick={runAnalysis}
            className="bg-indigo-600 hover:bg-indigo-500"
            data-testid="run-analysis-btn"
          >
            <Play className="h-4 w-4 mr-2" />
            Run Analysis
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (data.error) return null;

  const activeInsights = (data.insights || []).filter(i => !dismissed.has(i.id));
  const health = data.schedule_health || {};

  // Format cache time
  let cacheInfo = null;
  if (data.from_cache && data.cached_at) {
    try {
      const cachedDate = new Date(data.cached_at);
      const mins = Math.round((Date.now() - cachedDate.getTime()) / 60000);
      cacheInfo = mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
    } catch { /* ignore */ }
  }

  return (
    <div className="space-y-4" data-testid="ai-proactive-section">
      {/* Health bar */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-500" />
            <span className="text-sm font-semibold text-slate-800">AI Copilot</span>
          </div>
          <span className="text-xs text-slate-400">
            {data.period?.start} — {data.period?.end}
          </span>
          {cacheInfo && (
            <span className="text-[10px] text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">
              cached {cacheInfo}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {health.score != null && (
            <div className="flex items-center gap-1.5">
              <div className={`h-2 w-2 rounded-full ${
                health.score >= 80 ? 'bg-emerald-500' : health.score >= 60 ? 'bg-amber-500' : 'bg-red-500'
              }`} />
              <span className="text-xs font-medium text-slate-600" data-testid="health-score">
                Health: {health.score}/100
              </span>
            </div>
          )}
          {health.coverage_pct != null && (
            <span className="text-xs text-slate-500" data-testid="coverage-pct">
              Coverage: {health.coverage_pct}%
            </span>
          )}
          {health.upcoming_pto_count > 0 && (
            <span className="text-xs text-slate-500" data-testid="upcoming-pto">
              {health.upcoming_pto_count} PTO upcoming
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={runAnalysis}
            disabled={loading}
            className="h-7 text-xs"
            data-testid="refresh-ai-insights"
          >
            <RefreshCw className="h-3 w-3 mr-1" />Re-run
          </Button>
        </div>
      </div>

      {/* Summary */}
      {data.summary && (
        <p className="text-sm text-slate-600 -mt-1" data-testid="ai-summary">{data.summary}</p>
      )}

      {/* Insights */}
      {activeInsights.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-6 text-center">
            <ShieldCheck className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700">All clear for the next 2 weeks</p>
            <p className="text-xs text-slate-400 mt-1">No scheduling issues detected on operating days</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {activeInsights.map(insight => {
            const style = severityStyles[insight.severity] || severityStyles.info;
            const Icon = typeIcons[insight.type] || AlertTriangle;

            return (
              <Card
                key={insight.id}
                className={`border-0 border-l-4 ${style.border} ${style.bg} shadow-sm`}
                data-testid={`insight-${insight.id}`}
              >
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5 flex-1 min-w-0">
                      <Icon className={`h-4 w-4 ${style.text} mt-0.5 flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-800">
                            {insight.title}
                          </span>
                          <Badge variant={style.badge} className="text-[10px] h-4">
                            {insight.severity}
                          </Badge>
                          {insight.affected_date && (
                            <span className="text-[10px] text-slate-400">{insight.affected_date}</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                          {insight.description}
                        </p>
                        {insight.suggested_action && (
                          <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
                            <ChevronRight className="h-3 w-3" />
                            {insight.suggested_action}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {insight.action_type && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAction(insight)}
                          className="h-6 text-[10px] px-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                          data-testid={`insight-action-${insight.id}`}
                        >
                          Resolve
                        </Button>
                      )}
                      <button
                        onClick={() => dismiss(insight.id)}
                        className="text-slate-300 hover:text-slate-500 p-0.5"
                        data-testid={`dismiss-insight-${insight.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AIProactiveInsights;
